/**
 * RotationNudgePrompt — soft single-line prompt shown above the
 * compose bar when the peer's rotation cadence has slipped or gone
 * inactive. Gives the user a one-click "Nudge" action that sends a
 * short pre-written encrypted message asking them to rotate.
 *
 * Retention + public-good wrapper: the prompt converts passive
 * awareness (the avatar ring) into action. Every nudge that lands
 * trains another user on the platform to rotate, which makes our
 * own aggregate cryptographic story provably stronger over time.
 *
 * Guard rails:
 *   - 1:1 conversations only — nudging in groups would be noisy.
 *   - Hides in duress mode (via the useRotationHealthDetail hook,
 *     which already bails on the sessionStorage flag).
 *   - Per-peer dismissal persists in localStorage so a user who
 *     consciously says "not today" isn't re-nagged every refresh.
 *   - Post-nudge cooldown keyed on timestamp so the same user
 *     isn't hammered with daily reminders.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, Send, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useRotationHealthDetail } from '../hooks/useRotationHealth';
import { recordNudge } from '../utils/nudgeFeedback';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUDGE_BODY =
  "Friendly heads-up: your keys haven't rotated in a while. " +
  "Pop into Settings → Privacy → Rotate now to refresh them. " +
  "Takes five seconds; keeps our thread healthy. 🛡";

// 3-day cooldown between nudges for the same peer so the sender
// doesn't get stuck on a dead account either.
const COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000;

const _dismissKey = (peerId) => `ghostcomm_nudge_dismissed_${peerId}`;
const _lastSentKey = (peerId) => `ghostcomm_nudge_sent_at_${peerId}`;

const _readTs = (k) => {
  try {
    const v = localStorage.getItem(k);
    const n = v ? Number(v) : 0;
    return n && !Number.isNaN(n) ? n : 0;
  } catch { return 0; }
};

export const RotationNudgePrompt = ({
  peer,
  conversation,
  getParticipantPublicKeys,
  encryptMessage,
  onSent,
}) => {
  const { user } = useAuth();
  const detail = useRotationHealthDetail(peer?.id);
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(() => !!_readTs(_dismissKey(peer?.id)));

  if (!peer?.id || !conversation || conversation.type === 'group') return null;
  if (!detail) return null;
  if (detail.health !== 'slipping' && detail.health !== 'inactive') return null;
  if (dismissed) return null;

  // Post-send cooldown: don't re-prompt if we nudged this same peer
  // in the last COOLDOWN_MS window, even across refreshes.
  if (Date.now() - _readTs(_lastSentKey(peer.id)) < COOLDOWN_MS) return null;

  const daysLabel = detail.days === 1 ? '1 day' : `${detail.days} days`;
  const isInactive = detail.health === 'inactive';

  const dismiss = () => {
    try { localStorage.setItem(_dismissKey(peer.id), String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
  };

  const sendNudge = async () => {
    if (sending) return;
    setSending(true);
    try {
      const { keys: recipientKeys, pqBundles } = await getParticipantPublicKeys();
      const { encrypted_content, encrypted_keys, encrypted_keys_pq } =
        await encryptMessage(NUDGE_BODY, recipientKeys, pqBundles);
      const res = await axios.post(`${API}/messages`, {
        conversation_id: conversation.id,
        encrypted_content,
        encrypted_keys,
        encrypted_keys_pq,
      });
      try { localStorage.setItem(_lastSentKey(peer.id), String(Date.now())); } catch { /* noop */ }
      // Capture the peer's latest-rotation timestamp AT SEND time so
      // the feedback watcher can detect a new rotation laid down
      // after this moment (vs one that already existed before we
      // nudged — that wouldn't be "caused" by our nudge).
      recordNudge({
        peerId: peer.id,
        peerUsername: peer.username,
        peerLatestAtSent: detail?.latestAt || 0,
      });
      toast.success(`Nudge sent to ${peer.username}`);
      onSent?.(res.data, NUDGE_BODY);
      setDismissed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send nudge');
    } finally {
      setSending(false);
    }
  };

  const accent = isInactive
    ? 'border-rose-500/40 bg-rose-950/30 text-rose-200'
    : 'border-amber-500/40 bg-amber-950/30 text-amber-100';
  const iconColor = isInactive ? 'text-rose-300' : 'text-amber-300';

  return (
    <div
      className={`mx-4 mt-2 mb-1 rounded-md border ${accent} px-3 py-2 flex items-center gap-3 text-xs`}
      data-testid="rotation-nudge-prompt"
      data-peer-health={detail.health}
      role="status"
    >
      <AlertCircle className={`w-4 h-4 shrink-0 ${iconColor}`} />
      <div className="flex-1 leading-snug">
        <span className="font-medium">{peer.username}</span>
        {' '}hasn't rotated keys in {daysLabel}. Want to ping them?
      </div>
      <button
        type="button"
        onClick={sendNudge}
        disabled={sending || !user?.id}
        className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 px-2 py-1 text-[11px] transition-colors disabled:opacity-50"
        data-testid="rotation-nudge-send"
      >
        <Send className="w-3 h-3" />
        {sending ? 'Sending…' : 'Nudge'}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="text-white/50 hover:text-white transition-colors p-0.5"
        data-testid="rotation-nudge-dismiss"
        aria-label="Dismiss nudge prompt"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default RotationNudgePrompt;
